import { omit } from 'lodash';
import { ReactNode } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { REGEX } from '~constants/index';
import Input from './Input/Input';
import InputCheckbox from './InputCheckbox/InputCheckbox';
import InputDateRange from './InputDateRange/InputDateRange';
import InputDropzone from './InputDropzone/InputDropzone';
import InputSelect from './InputSelect/InputSelect';
import InputSwitch from './InputSwitch/InputSwitch';
import InputTextArea from './InputTextArea/InputTextArea';

const InputHandler = ({
  type,
  name,
  nameStartDate,
  nameEndDate,
  label,
  placeholder,
  iconName,
  labelHelper,
  disabled = false,
  isLoading = false,
  readOnly = false,
  isMulti = false,
  isSearchable = true,
  step,
  link,
  design,
  defaultValue,
  filePreSelected,
  rules = {},
  options = [],
  extensions = [],
  control,
  min,
  max,
  onInputChange = () => {},
  onEventChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  debounceTime = 300
}: InputHandlerProps) => {
  const { t } = useTranslation();
  let valueByDefault = defaultValue;
  if (type === 'select') {
    if (Array.isArray(defaultValue)) {
      valueByDefault = defaultValue.map((item) => item.value).join(',');
    } else if (defaultValue) {
      const newDefaultValue = defaultValue as SelectData<string | number>;
      valueByDefault = newDefaultValue.value;
    }
  } else if (type === 'date-range') {
    const valueSplitted =
      typeof defaultValue === 'string' ? defaultValue.split(',') : '';
    valueByDefault = defaultValue && {
      [nameStartDate!]: valueSplitted[0],
      [nameEndDate!]: valueSplitted[1]
    };
  }

  const rulesWithoutValidates = omit(rules, ['validate']);
  const isInjection = (value: string) => {
    if (REGEX.injection_pattern.test(value)) return true;
    return t('form_errors.characters_not_allow_found');
  };

  const readonlyInput = [
    'text',
    'number',
    'date',
    'datetime-local',
    'password',
    'checkbox',
    'text-area'
  ];
  if (readOnly && readonlyInput.includes(type)) {
    //to avoid overlap disabled
    disabled = false;
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...rulesWithoutValidates,
        validate: { isInjection, ...rules.validate }
      }}
      shouldUnregister={true}
      defaultValue={valueByDefault}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        let InputDisplayed: ReactNode;
        switch (type) {
          case 'text':
          case 'number':
          case 'date':
          case 'datetime-local':
          case 'password':
            InputDisplayed = (
              <Input
                debounceTime={debounceTime}
                type={type}
                name={name}
                labelHelper={labelHelper}
                label={label}
                step={step}
                placeholder={placeholder}
                value={value as InputTextDefaultValue}
                defaultValue={defaultValue as InputTextDefaultValue}
                iconName={iconName}
                disabled={disabled}
                readOnly={readOnly}
                design={design}
                required={rules?.required?.value ?? false}
                hookError={error}
                min={min}
                max={max}
                hookOnChange={onChange}
                onInputChange={onInputChange}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            );
            break;
          case 'checkbox':
            InputDisplayed = (
              <InputCheckbox
                name={name}
                labelHelper={labelHelper}
                label={label}
                placeholder={placeholder}
                defaultValue={defaultValue as boolean}
                disabled={disabled}
                readOnly={readOnly}
                required={rules?.required?.value ?? false}
                design={design}
                link={link}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
              />
            );
            break;
          case 'text-area':
            InputDisplayed = (
              <InputTextArea
                debounceTime={debounceTime}
                name={name}
                labelHelper={labelHelper}
                label={label}
                placeholder={placeholder}
                value={value as InputTextDefaultValue}
                defaultValue={defaultValue as InputTextDefaultValue}
                disabled={disabled}
                readOnly={readOnly}
                required={rules?.required?.value ?? false}
                design={design}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            );
            break;
          case 'select':
            InputDisplayed = (
              <InputSelect
                name={name}
                labelHelper={labelHelper}
                label={label}
                placeholder={placeholder}
                options={options}
                isMulti={isMulti}
                defaultValue={defaultValue as SelectData | SelectData[]}
                iconName={iconName}
                isSearchable={isSearchable}
                disabled={disabled}
                required={rules?.required?.value ?? false}
                design={design}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
                onEventChange={onEventChange}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            );
            break;
          case 'dropzone':
            InputDisplayed = (
              <InputDropzone
                isLoading={isLoading}
                name={name}
                labelHelper={labelHelper}
                label={label}
                extensions={extensions}
                disabled={disabled}
                required={rules?.required?.value ?? false}
                design={design}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
                filePreSelected={filePreSelected}
                defaultValue={defaultValue as string}
              />
            );
            break;
          case 'switch':
            InputDisplayed = (
              <InputSwitch
                name={name}
                label={label}
                disabled={disabled}
                defaultValue={defaultValue as boolean}
                required={rules?.required?.value ?? false}
                design={design}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
              />
            );
            break;
          case 'date-range':
            InputDisplayed = (
              <InputDateRange
                name={name}
                label={label}
                defaultValueStartDate={
                  typeof defaultValue === 'string'
                    ? defaultValue.split(',')[0]
                    : undefined
                }
                defaultValueEndDate={
                  typeof defaultValue === 'string'
                    ? defaultValue.split(',')[1]
                    : undefined
                }
                nameStartDate={nameStartDate}
                nameEndDate={nameEndDate}
                min={min}
                max={max}
                disabled={disabled}
                design={design}
                required={rules?.required?.value ?? false}
                hookError={error}
                hookOnChange={onChange}
                onInputChange={onInputChange}
              />
            );
            break;
          default:
            return (InputDisplayed = <></>);
        }
        return InputDisplayed;
      }}
    />
  );
};

export default InputHandler;
